/**
 * Authentication helpers (ADR-0007): primary OAuth sign-in, email/password
 * fallback, and owner step-up to assurance level aal2 with a TOTP factor.
 * No credentials or provider secrets live in this code; provider setup is
 * configured in the identity service, never here.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { STEP_UP_RPC_NAMES, type BrowserRpcName } from './rpc';

/** OAuth provider identifier locked by ADR-0007. */
export const PRIMARY_OAUTH_PROVIDER = 'google' as const;

export type AssuranceLevel = 'aal1' | 'aal2';

export interface AssuranceState {
  readonly current: AssuranceLevel | null;
  readonly next: AssuranceLevel | null;
}

export type StepUpNeed = 'none' | 'verify' | 'enroll';

/** How the owner chose to confirm protected actions (brunel.owner_security_preferences). */
export type StepUpMethod = 'authenticator' | 'password';

/** The database accepts a password sign-in this recent when the owner chose the password. */
export const PASSWORD_STEP_UP_WINDOW_SECONDS = 900;

/**
 * Decide what a privileged action needs before it may be submitted. `recentPassword` is true
 * only when the owner chose the password and this session's password sign-in is still recent.
 */
export function stepUpNeed(name: BrowserRpcName, assurance: AssuranceState, recentPassword = false): StepUpNeed {
  if (!STEP_UP_RPC_NAMES.includes(name)) return 'none';
  if (assurance.current === 'aal2' || recentPassword) return 'none';
  return assurance.next === 'aal2' ? 'verify' : 'enroll';
}

/**
 * Seconds since this session's password sign-in, read from its token's `amr` claim - the same
 * claim the database checks. Null when the session was not opened with a password.
 */
export function passwordSignInAgeSeconds(accessToken: string | null | undefined, nowMs = Date.now()): number | null {
  if (!accessToken) return null;
  try {
    const part = accessToken.split('.')[1] ?? '';
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { amr?: unknown };
    if (!Array.isArray(payload.amr)) return null;
    const times = payload.amr
      .filter((entry): entry is { method: string; timestamp: number } =>
        typeof entry === 'object' && entry !== null && (entry as { method?: unknown }).method === 'password'
        && typeof (entry as { timestamp?: unknown }).timestamp === 'number')
      .map((entry) => entry.timestamp);
    if (times.length === 0) return null;
    return Math.max(0, Math.floor(nowMs / 1000) - Math.max(...times));
  } catch {
    return null;
  }
}

/** True when a password entered now still passes the owner gate (a small margin short of it). */
export function passwordStepUpFresh(accessToken: string | null | undefined, nowMs = Date.now()): boolean {
  const age = passwordSignInAgeSeconds(accessToken, nowMs);
  return age !== null && age < PASSWORD_STEP_UP_WINDOW_SECONDS - 30;
}

/** The signed-in owner's own choice; the authenticator unless they chose the password. */
export async function readStepUpMethod(client: SupabaseClient): Promise<StepUpMethod> {
  const { data, error } = await client.schema('brunel').from('owner_security_preferences')
    .select('step_up_method').limit(1).maybeSingle();
  if (error || !data) return 'authenticator';
  return (data as { step_up_method?: unknown }).step_up_method === 'password' ? 'password' : 'authenticator';
}

/** Confirm with the account password: a fresh password sign-in for the same account. */
export async function confirmWithPassword(client: SupabaseClient, email: string, password: string): Promise<string | null> {
  if (password.length === 0) return 'Enter your password.';
  const { error } = await client.auth.signInWithPassword({ email, password });
  return error ? 'That password did not work.' : null;
}

/** True when a trimmed string is shaped like an email address. */
export function validateEmail(email: string): string | null {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email address.';
  return null;
}

export function validateEmailPassword(email: string, password: string): string | null {
  const invalid = validateEmail(email);
  if (invalid) return invalid;
  if (password.length === 0) return 'Enter your password.';
  return null;
}

export async function signInWithOAuth(client: SupabaseClient, redirectTo: string): Promise<string | null> {
  const { error } = await client.auth.signInWithOAuth({ provider: PRIMARY_OAUTH_PROVIDER, options: { redirectTo } });
  if (!error) return null;
  const code = typeof error.code === 'string' && error.code !== '' ? error.code : null;
  const status = typeof error.status === 'number' ? error.status : null;
  if (code) return `Google sign-in could not start (${code}).`;
  if (status) return `Google sign-in could not start (HTTP ${status}).`;
  return 'Google sign-in could not start. Try again.';
}

function signInErrorMessage(error: { code?: string | null }): string {
  switch (error.code ?? '') {
    case 'invalid_credentials':
      return 'Email or password is incorrect.';
    case 'weak_password':
      return 'Supabase rejected this password under the current password policy.';
    case 'email_not_confirmed':
      return 'This email address still needs to be confirmed.';
    case 'user_banned':
      return 'This account is currently blocked.';
    case 'captcha_failed':
      return 'Sign-in was blocked by the CAPTCHA configuration.';
    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit':
      return 'Too many sign-in attempts. Wait a moment and try again.';
    default:
      return error.code ? `Sign-in failed (${error.code}).` : 'Sign-in failed. Try again.';
  }
}

export async function signInWithPassword(client: SupabaseClient, email: string, password: string): Promise<string | null> {
  const invalidEmail = validateEmail(email);
  if (invalidEmail) return invalidEmail;
  if (password.length === 0) return 'Enter your password.';
  const { error } = await client.auth.signInWithPassword({ email: email.trim(), password });
  return error ? signInErrorMessage(error) : null;
}

export async function signUpWithPassword(client: SupabaseClient, email: string, password: string, redirectTo: string): Promise<string | null> {
  const invalid = validateEmailPassword(email, password);
  if (invalid) return invalid;
  const { error } = await client.auth.signUp({ email: email.trim(), password, options: { emailRedirectTo: redirectTo } });
  return error ? 'Account could not be created.' : null;
}

/** The only reply a reset request may give: it never says whether the account exists. */
export const PASSWORD_RESET_SENT = 'If an account exists for that email, a password reset link has been sent.';

/**
 * Email a password reset link to the address, returning to the configured
 * Brunel origin afterwards. The answer is the same neutral sentence whether
 * the request was accepted or refused, because anything else would let a
 * stranger learn which emails own a Brunel organization.
 */
export async function requestPasswordReset(client: SupabaseClient, email: string, redirectTo: string): Promise<string> {
  const invalid = validateEmail(email);
  if (invalid) return invalid;
  const { error } = await client.auth.resetPasswordForEmail(email.trim(), { redirectTo });
  if (!error) return PASSWORD_RESET_SENT;

  // Never reveal whether an arbitrary address exists. Configuration/delivery failures,
  // however, must be visible to the owner or Brunel turns a broken reset path into a lie.
  const code = typeof error.code === 'string' && error.code !== '' ? error.code : null;
  if (code === 'user_not_found' || code === 'invalid_credentials') return PASSWORD_RESET_SENT;
  if (code) return `Password reset could not be sent (${code}).`;
  const status = typeof error.status === 'number' ? error.status : null;
  return status ? `Password reset could not be sent (HTTP ${status}).` : 'Password reset could not be sent. Try again.';
}

/** True when the identity service says the browser came back from a reset email. */
export function isPasswordRecovery(event: string): boolean {
  return event === 'PASSWORD_RECOVERY';
}

/** Check that a new password is present and matches its confirmation. */
export function validateNewPassword(newPassword: string, confirm: string): string | null {
  if (newPassword.length === 0) return 'Enter a password.';
  if (newPassword !== confirm) return 'The passwords do not match.';
  return null;
}

/** Set a new password for the signed-in owner; a recovery session is one. */
export async function updatePassword(client: SupabaseClient, newPassword: string, confirm: string): Promise<string | null> {
  const invalid = validateNewPassword(newPassword, confirm);
  if (invalid) return invalid;
  const { error } = await client.auth.updateUser({ password: newPassword });
  return error ? 'The password could not be updated. Try again.' : null;
}

export async function readAssurance(client: SupabaseClient): Promise<AssuranceState> {
  const { data, error } = await client.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return { current: null, next: null };
  return { current: data.currentLevel as AssuranceLevel | null, next: data.nextLevel as AssuranceLevel | null };
}

export interface TotpEnrollment {
  readonly factorId: string;
  /** SVG QR code for an authenticator app; shown once, never stored. */
  readonly qrCode: string;
}

export async function enrollTotp(client: SupabaseClient): Promise<TotpEnrollment | null> {
  const { data, error } = await client.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Brunel owner' });
  if (error || !data || data.type !== 'totp') return null;
  return { factorId: data.id, qrCode: data.totp.qr_code };
}

export async function firstVerifiedTotpFactor(client: SupabaseClient): Promise<string | null> {
  const { data, error } = await client.auth.mfa.listFactors();
  if (error || !data) return null;
  return data.totp.find((factor) => factor.status === 'verified')?.id ?? null;
}

/** Verify a six-digit code; on success the session is upgraded to aal2. */
export async function verifyTotp(client: SupabaseClient, factorId: string, code: string): Promise<string | null> {
  if (!/^\d{6}$/.test(code)) return 'Enter the 6-digit code.';
  const { error } = await client.auth.mfa.challengeAndVerify({ factorId, code });
  return error ? 'That code did not work.' : null;
}

/**
 * WHICH DOOR A SESSION CAME THROUGH, AND WHAT THAT MEANS FOR A DESK WITH NO ORGANIZATION.
 *
 * The owner's account is an email and a password. Signing in with Google produces a session
 * too - and unless the identity service has linked that Google identity to that same user,
 * it is a DIFFERENT user: a real, valid, signed-in account with no membership of anything.
 *
 * Brunel already has a screen for a signed-in account with no organization. It invites the
 * person to create one and makes them its owner. Shown to an unlinked Google session that
 * would quietly manufacture a second owner of a second organization, next to the real one,
 * and the owner would be left signing in to whichever he happened to get.
 *
 * So the question "how did this session authenticate" is answered from what the identity
 * service recorded - `app_metadata.provider`, and the identities on the user - and never
 * from the shape of an email or anything else guessed at. What is done about the answer is
 * decided in `entryDecision`, away from the screen, so both can be tested without a browser.
 *
 * Nothing here links, creates or alters an identity. It decides which screen to show.
 */

/** How the session in hand was authenticated. */
export type SignInMethod = 'password' | 'oauth' | 'unknown';

/** Just enough of a Supabase user to answer the question, so tests need no client. */
export interface SessionUserFacts {
  readonly email?: string | null;
  readonly app_metadata?: { readonly provider?: unknown; readonly providers?: unknown } | null;
  readonly identities?: readonly { readonly provider?: unknown }[] | null;
}

function providerOf(user: SessionUserFacts): string | null {
  const provider = user.app_metadata?.provider;
  if (typeof provider === 'string' && provider.trim() !== '') return provider.trim().toLowerCase();
  // A user with exactly one identity has authenticated through it, whatever the metadata
  // does or does not carry; more than one and only the metadata knows which was used.
  const identities = user.identities ?? [];
  if (identities.length === 1) {
    const only = identities[0]?.provider;
    if (typeof only === 'string' && only.trim() !== '') return only.trim().toLowerCase();
  }
  return null;
}

/**
 * The door this session came through. `email` is the identity service's own name for the
 * email/password identity; anything else it names is an external provider.
 */
export function signInMethod(user: SessionUserFacts | null | undefined): SignInMethod {
  if (user === null || user === undefined) return 'unknown';
  const provider = providerOf(user);
  if (provider === null) return 'unknown';
  return provider === 'email' ? 'password' : 'oauth';
}

/** Every identity the service says is on this user, lower-cased, in the order it gave them. */
export function linkedProviders(user: SessionUserFacts | null | undefined): readonly string[] {
  const named = Array.isArray(user?.app_metadata?.providers)
    ? (user.app_metadata.providers as readonly unknown[])
    : (user?.identities ?? []).map((identity) => identity?.provider);
  return named
    .filter((provider): provider is string => typeof provider === 'string' && provider.trim() !== '')
    .map((provider) => provider.trim().toLowerCase());
}

/** The address to show the person, so they can see which account they are actually in. */
export function signedInEmail(user: SessionUserFacts | null | undefined): string | null {
  const email = user?.email;
  return typeof email === 'string' && email.trim() !== '' ? email.trim() : null;
}

export type EntryDecision =
  /** A member of something: straight into Brunel, as always. */
  | 'enter'
  /** Nobody's member, and they signed in with a password: the first-run bootstrap, as always. */
  | 'bootstrap'
  /** Nobody's member, and they signed in through a provider: this is where a second owner
   *  would have been created, so nothing is offered but a way back. */
  | 'unlinked_oauth';

/**
 * Which screen a signed-in session gets.
 *
 * Membership decides it first: an account that belongs to an organization is in, however it
 * signed in, so a Google identity the owner HAS linked behaves exactly as it did before.
 * Only an account belonging to nothing is a question, and then the door it came through is
 * the answer. An unknown door is treated as the provider case, because the cost of being
 * wrong in that direction is a screen the owner reads, and the cost the other way is a
 * second owner of a second organization that nobody meant to create.
 */
export function entryDecision(input: {
  readonly organizationCount: number;
  readonly method: SignInMethod;
}): EntryDecision {
  if (input.organizationCount > 0) return 'enter';
  return input.method === 'password' ? 'bootstrap' : 'unlinked_oauth';
}
